import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Select, {
  GroupBase,
  OnChangeValue,
  OptionProps,
  PropsValue,
  Props as SelectProps,
  SingleValueProps,
} from "react-select";

import { hasValue } from "@examspark/common";
import { Replace } from "@examspark/types";

import { FormContext } from "../../contexts/FormContext";

import {
  RichTextOption,
  RichTextSingleValue,
  OptionWithBadge,
  OptionWithCheckbox,
  selectInputStyles,
} from "./select";

export type Option<TValue extends string | null = string> = {
  label: string;
  value: TValue;
  badgeCount?: number;
};

export type Options<
  TValue extends string | null = string,
  TOption extends Option<TValue> = Option<TValue>,
> = Array<TOption | GroupBase<TOption>>;

type Props<
  TValue extends string | null,
  TOption extends Option<TValue>,
  TIsMulti extends boolean = false,
> = Omit<
  SelectProps<TOption, TIsMulti, GroupBase<TOption>>,
  "options" | "value" | "onChange" | "isDisabled" | "isMulti"
> & {
  options?: Options<TValue, TOption>;
  value?: TIsMulti extends true ? (TValue | null)[] : TValue | null;
  onChange?: (option: OnChangeValue<TOption, TIsMulti>) => void;
  disabled?: boolean;
  readonlyOnSingleValue?: boolean;
  isMulti?: TIsMulti;
  useCheckboxesForMultiSelect?: boolean;
  useRichTextOptions?: boolean;
  useBadgeForOptions?: boolean;
};

/**
 * Standard `react-select` component that handles values and changes to those
 * values a bit more graefullly than the standard component.
 *
 * Developer Tip: If you need to prevent blur from closing menu so you can
 * inspect the element, remove the `focusout` event in your browser.
 * */
const SelectInput = <
  TValue extends string | null,
  TOption extends Option<TValue> = Option<TValue>,
  TIsMulti extends boolean = false,
>({
  value,
  onChange,
  disabled,
  readonlyOnSingleValue,
  useCheckboxesForMultiSelect,
  styles,
  id,
  ...props
}: Props<TValue, TOption, TIsMulti>) => {
  const form = useContext(FormContext);

  const optionsMap = useMemo(() => {
    return new Map<TValue, Option<TValue>>(
      iterateOptions(props.options)?.map((option) => [option.value, option]),
    );
  }, [props.options]);

  const findOptionsFromValue = useCallback(
    (value: PropsValue<TValue | null | undefined>) => {
      if (value == null) return null;

      if (Array.isArray(value)) {
        return value.map((o) => optionsMap.get(o)).filter(hasValue);
      }

      //
      // This will only happen in the case of single-selects, so we have to
      // convince TS that's what we're doing
      //
      return optionsMap.get(value as TValue) ?? null;
    },
    [optionsMap],
  );

  const onChangeInternal = useCallback(
    (option: OnChangeValue<TOption, TIsMulti>) => {
      setSelectedOptions(option);
      return onChange?.(option);
    },
    [onChange],
  );

  const [singleValue, setSingleValue] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<
    OnChangeValue<TOption, TIsMulti>
  >(findOptionsFromValue(value ?? null) as OnChangeValue<TOption, TIsMulti>);

  const optionsFromValue = useMemo(
    () =>
      findOptionsFromValue(value ?? null) as OnChangeValue<TOption, TIsMulti>,
    [findOptionsFromValue, value],
  );

  useEffect(() => {
    if (optionsFromValue == null && selectedOptions != null) {
      onChangeInternal(optionsFromValue);
    }
    setSelectedOptions(optionsFromValue);
  }, [selectedOptions, onChangeInternal, optionsFromValue]);

  useEffect(() => {
    if (
      !readonlyOnSingleValue ||
      props.options?.length !== 1 ||
      isOptionGroup(props.options[0])
    ) {
      setSingleValue(false);
      return;
    }

    setSingleValue(true);

    if (selectedOptions !== props.options[0]) {
      onChangeInternal(props.options[0] as OnChangeValue<TOption, TIsMulti>);
    }
  }, [
    readonlyOnSingleValue,
    props.options,
    setSelectedOptions,
    setSingleValue,
    selectedOptions,
    onChangeInternal,
  ]);

  const menuPortal = document.querySelector<HTMLElement>("#root");

  if (!menuPortal) throw Error("No root element");

  const componentsOverrides: SelectProps<TOption>["components"] = {};
  if (props.isMulti && useCheckboxesForMultiSelect) {
    componentsOverrides.Option = OptionWithCheckbox as React.ComponentType<
      React.PropsWithChildren<OptionProps<TOption>>
    >;
  } else if (props.useBadgeForOptions) {
    componentsOverrides.Option = OptionWithBadge as React.ComponentType<
      React.PropsWithChildren<OptionProps<TOption>>
    >;
  } else if (props.useRichTextOptions) {
    componentsOverrides.Option = RichTextOption as React.ComponentType<
      React.PropsWithChildren<OptionProps<TOption>>
    >;
    componentsOverrides.SingleValue =
      RichTextSingleValue as React.ComponentType<SingleValueProps<TOption>>;
  }

  const fullStyles = { ...selectInputStyles, ...styles };

  return (
    <Select
      styles={fullStyles}
      menuPlacement="auto"
      components={componentsOverrides}
      {...props}
      inputId={id}
      value={selectedOptions as PropsValue<TOption>}
      onChange={(e) => onChangeInternal(e as OnChangeValue<TOption, TIsMulti>)}
      isDisabled={(disabled ?? form.disabled) || singleValue}
      // This allows the select to break out of modals
      menuPortalTarget={menuPortal}
    />
  );
};

export function iterateOptions<
  TValue extends string | null = string,
  TGroup extends Option<TValue> = Option<TValue>,
>(
  options: Options<TValue, TGroup> | undefined,
): Array<Option<TValue> & { group?: TGroup }> | undefined {
  return options?.flatMap((option) => {
    if (isOptionGroup(option)) {
      return option.options.map((groupOption) => ({
        ...groupOption,
        group: option,
      }));
    }
    return option;
  });
}

export function isOptionGroup<TOption, TGroup extends GroupBase<TOption>>(
  option: TOption | TGroup,
): option is TGroup {
  if (option == null) return false;
  if (typeof option !== "object") return false;
  return "options" in option;
}

type SelectInputSimpleProps = Replace<
  Props<string, Option<string>>,
  {
    onChange?: (value: string | null) => void;
  }
>;

/**
 * Convenience `react-select` component whose `onChange` method directly returns
 * the `e.value` or `null`.
 */
const SelectInputSimple = ({ onChange, ...props }: SelectInputSimpleProps) => (
  <SelectInput {...props} onChange={(e) => onChange?.(e?.value ?? null)} />
);

SelectInput.Simple = SelectInputSimple;

export default SelectInput;
